import { isNeedsReauthResponse } from '@core/api-clients/me-client.js';
import { queryClient } from '@core/query/client.js';
import { meKeys } from '@core/query/keys.js';
import type { MeResponse, NeedsReauthResponse } from '@noma-eco/platform/schema';

export type Profile = { userId: string | null; isInternalOrTest: boolean };

/**
 * Vanilla projection of the `me` query into `{ userId }`, so non-React code
 * (the tag system's PostHog + Sentry tags) can subscribe to identity changes
 * without rendering a bridge component. Reads + watches the same cache slot
 * that `useMe()` populates — there is no parallel store and no
 * dual-source-of-truth syncing.
 *
 * `subscribeProfile(fn)` invokes `fn` immediately with the current state, so
 * tags don't need an extra `getProfile()` call after subscribing.
 *
 * When `me` returns the `NeedsReauthResponse` sentinel (orgless first-sign-in
 * — see `account-model/design.md` §7 Leg 2), we treat it as "no user yet" so
 * tags don't identify against a half-bootstrapped session.
 *
 * Implementation note: we subscribe to the QueryCache (not a `QueryObserver`)
 * because `removeQueries` invalidates a long-lived observer's internal query
 * reference; cache events keep firing through query teardown and re-creation,
 * which is the right shape for a module-level listener.
 */
const meQueryKey = meKeys.all;
const queryCache = queryClient.getQueryCache();

type MaybeMe = MeResponse | NeedsReauthResponse | undefined;

function projectProfile(data: MaybeMe): Profile {
  if (!data || isNeedsReauthResponse(data)) {
    return { userId: null, isInternalOrTest: false };
  }
  return { userId: data.user.id, isInternalOrTest: data.user.isInternalOrTest };
}

function readCurrentMe(): MaybeMe {
  return queryCache.find<MeResponse | NeedsReauthResponse>({ queryKey: meQueryKey })
    ?.state.data;
}

function isMeQueryKey(key: readonly unknown[]): boolean {
  return (
    key.length === meQueryKey.length && key.every((part, i) => part === meQueryKey[i])
  );
}

export function getProfile(): Profile {
  return projectProfile(readCurrentMe());
}

export function subscribeProfile(fn: (profile: Profile) => void): () => void {
  let last: Profile | undefined; // undefined = never emitted
  const emit = (next: Profile) => {
    if (
      last &&
      next.userId === last.userId &&
      next.isInternalOrTest === last.isInternalOrTest
    )
      return;
    last = next;
    fn(next);
  };
  emit(getProfile());
  return queryCache.subscribe((event) => {
    if (!isMeQueryKey(event.query.queryKey)) return;
    // On 'removed' the destroyed query still carries its last data, so read
    // through readCurrentMe() to get undefined when the cache no longer
    // holds the me-query.
    emit(projectProfile(readCurrentMe()));
  });
}
